import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next';

import { useCallback, useMemo } from 'react';

import { Box, HStack, useSlotRecipe } from '@chakra-ui/react';

import { RegexConst } from '@nocowanie/core/consts';
import { IconsLinear } from '@nocowanie/icons/linear';

import { EllipsisInput } from '@app/components/atoms/ellipsis-input';
import { PageButton } from '@app/components/atoms/page-button';
import { PaginationArrow } from '@app/components/atoms/pagination-arrow';
import { calculateVisiblePages, EllipsisType } from '@app/helpers/pagination.helpers';
import * as listingStateSlice from '@app/state/slices/listing-state.slice';
import { setPageAction } from '@app/state/slices/search-state.actions';
import * as searchStateSlice from '@app/state/slices/search-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { PaginationProps } from './pagination.props';

export const Pagination = ({ initialPageCount, ...props }: PaginationProps) => {
    const dispatch = useAppDispatch();
    const recipe = useSlotRecipe({ key: 'pagination' });
    const styles = recipe({});
    const { t } = useTranslation('listing', { keyPrefix: 'pagination' });
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const currentPage = useAppSelector(searchStateSlice.selectors.selectPage);
    const { pageCount: storePageCount } = useAppSelector(
        listingStateSlice.selectors.selectListingMeta,
    ).data;

    const pageCount = useMemo(
        () => (storePageCount > 1 ? storePageCount : (initialPageCount ?? storePageCount)),
        [storePageCount, initialPageCount],
    );

    const isCampaignListingPage = RegexConst.CAMPAIGN_PAGE_TYPE.test(pathname ?? '');

    const buildPageUrl = useCallback(
        (page: number): string => {
            const params = new URLSearchParams(searchParams?.toString() ?? '');

            if (!isCampaignListingPage) {
                params.delete('location');
                params.delete('geoId');
                params.delete('type');
            }

            if (page > 1) {
                params.set('page', page.toString());
            } else {
                params.delete('page');
            }

            const queryString = params.toString();
            const currentPath = pathname || '/';
            return queryString ? `${currentPath}?${queryString}` : currentPath;
        },
        [pathname, searchParams, isCampaignListingPage],
    );

    const handlePageChange = useCallback(
        (page: number, shouldUpdateUrl = true) => {
            if (page < 1 || page > pageCount || page === currentPage) {
                return;
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });

            setTimeout(() => {
                dispatch(setPageAction({ page, shouldUpdateUrl }));
            }, 0);
        },
        [currentPage, pageCount],
    );

    const pageButtons = useMemo(
        () => calculateVisiblePages(currentPage, pageCount),
        [currentPage, pageCount],
    );

    const isLastPageStatic = currentPage <= pageCount - 4;

    if (pageCount <= 1) {
        return null;
    }

    return (
        <HStack
            as={'nav'}
            gap={{ base: 2, md: 4 }}
            css={styles.root}
            data-testid={'pagination-wrapper'}
            {...props}
        >
            <PaginationArrow
                isDisabled={currentPage === 1}
                ariaLabel={t('firstPage')}
                icon={<IconsLinear.ArrowEnd />}
                pageUrl={buildPageUrl(1)}
                onPageChange={() => handlePageChange(1, false)}
                testId={'pagination-first-page-btn'}
            />

            <PaginationArrow
                isDisabled={currentPage === 1}
                ariaLabel={t('previousPage')}
                icon={<IconsLinear.ArrowLeft2 />}
                pageUrl={buildPageUrl(currentPage - 1)}
                onPageChange={() => handlePageChange(currentPage - 1, false)}
                testId={'pagination-prev-page-btn'}
            />

            {pageButtons.map((page, index) => {
                if (page === EllipsisType.START || page === EllipsisType.END) {
                    return (
                        <EllipsisInput
                            key={`${page}-${index}`}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            data-testid={'pagination-custom-page'}
                        />
                    );
                }

                const isLastPage = page === pageCount;
                if (isLastPage && isLastPageStatic) {
                    return (
                        <Box key={page} as={'span'} css={styles.staticPage}>
                            {page}
                        </Box>
                    );
                }

                return (
                    <PageButton
                        key={page}
                        page={page}
                        isCurrent={page === currentPage}
                        ariaLabel={t('goToPage', { page })}
                        pageUrl={buildPageUrl(page)}
                        onPageChange={() => handlePageChange(page, false)}
                    />
                );
            })}

            <PaginationArrow
                isDisabled={currentPage === pageCount}
                ariaLabel={t('nextPage')}
                icon={<IconsLinear.ArrowRight2 />}
                pageUrl={buildPageUrl(currentPage + 1)}
                onPageChange={() => handlePageChange(currentPage + 1, false)}
                testId={'pagination-next-page-btn'}
            />

            <PaginationArrow
                isDisabled={currentPage === pageCount}
                ariaLabel={t('lastPage')}
                icon={<IconsLinear.ArrowEnd rotate={'180deg'} />}
                pageUrl={buildPageUrl(pageCount)}
                onPageChange={() => handlePageChange(pageCount, false)}
                testId={'pagination-last-page-btn'}
            />
        </HStack>
    );
};
